// Building, downloading and opening the mileage log, shared by Export, Mileage and the printable
// report (which rebuilds the log from its link in a new tab).

import type { AccountData } from "../data/AccountData.tsx";
import { displayUnit } from "../data/derived.ts";
import type { Category } from "../domain/categories.ts";
import { mileageCSV } from "../domain/csv.ts";
import { type Interval, type PeriodKind, customPeriod, periodInterval, periodTitle } from "../domain/periods.ts";
import { MileageReport } from "../domain/report.ts";

export interface LogChoice {
  periodKind: Exclude<PeriodKind, "all">;
  /** For custom periods, "2026-09-01". */
  from?: string;
  to?: string;
  vehicleId: string;
  categories: string[];
}

function parseDay(text: string | undefined): Date | undefined {
  const [year, month, day] = (text ?? "").split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : undefined;
}

/** "September 2026" for a calendar month, otherwise the period's title or range. */
export function logTitle(kind: LogChoice["periodKind"], interval: Interval): string {
  const lastDay = new Date(interval.end.getTime() - 1);
  const isOneMonth = interval.start.getDate() === 1 && interval.start.getMonth() === lastDay.getMonth()
    && interval.start.getFullYear() === lastDay.getFullYear() && interval.end.getDate() === 1;
  if (isOneMonth) return interval.start.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const isOneYear = interval.start.getMonth() === 0 && interval.start.getDate() === 1 && lastDay.getMonth() === 11 && lastDay.getDate() === 31
    && interval.start.getFullYear() === lastDay.getFullYear();
  if (isOneYear) return String(interval.start.getFullYear());
  return kind === "custom" ? periodTitle({ kind, interval }) : periodTitle({ kind });
}

export function choiceInterval(choice: LogChoice, now: Date): Interval {
  if (choice.periodKind === "custom") {
    return customPeriod(parseDay(choice.from) ?? now, parseDay(choice.to) ?? now).interval!;
  }
  return periodInterval({ kind: choice.periodKind }, now)!;
}

export function buildReport(data: AccountData, choice: LogChoice, now = new Date()): MileageReport {
  const interval = choiceInterval(choice, now);
  const vehicle = choice.vehicleId === "all" ? null : data.vehicles.find((candidate) => candidate.id === choice.vehicleId) ?? null;
  const categories = choice.categories.map((raw) => data.catalog.resolve(raw)).filter((category): category is Category => !!category);
  return new MileageReport({
    trips: data.trips,
    vehicles: data.vehicles,
    catalog: data.catalog,
    rates: data.rates,
    interval,
    periodTitle: logTitle(choice.periodKind, interval),
    vehicle,
    categories,
    unit: displayUnit(data, vehicle?.id),
    generatedAt: now,
  });
}

export function downloadCSV(report: MileageReport): void {
  const blob = new Blob([mileageCSV(report)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${report.baseFileName}.csv`;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function reportPath(choice: LogChoice): string {
  const query = new URLSearchParams({ period: choice.periodKind, vehicle: choice.vehicleId, categories: choice.categories.join(",") });
  if (choice.periodKind === "custom") {
    query.set("from", choice.from ?? "");
    query.set("to", choice.to ?? "");
  }
  return `/report?${query.toString()}`;
}

export function choiceFromQuery(query: URLSearchParams, data: AccountData): LogChoice {
  const kinds = ["thisMonth", "lastMonth", "lastQuarter", "thisYear", "lastYear", "custom"] as const;
  const period = query.get("period");
  const periodKind = kinds.find((kind) => kind === period) ?? "lastMonth";
  const categories = query.get("categories");
  return {
    periodKind,
    from: query.get("from") ?? undefined,
    to: query.get("to") ?? undefined,
    vehicleId: query.get("vehicle") ?? "all",
    categories: categories ? categories.split(",").filter(Boolean) : data.catalog.deductible.map((category) => category.rawValue),
  };
}

export function openReport(choice: LogChoice): void {
  window.open(reportPath(choice), "_blank", "noopener");
}
